import React, { useEffect, useRef, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Text
} from 'recharts';
import { formatPercent } from 'utils';
import { emptyColor } from '../Utils/';
import useOnScreen from './UseOnScreen';
import { useTheme } from '@material-ui/styles';
import { radiusVertical } from 'utils/charts';
import { tooltipStylePadded } from '../Tooltips/utils';

const dummyData = [
  { name: 'In a very good year', value: 0 },
  { name: 'In a good year', value: 0 },
  { name: 'On average', value: 0 },
  { name: 'In a bad Year', value: 0 },
  { name: 'In a very Bad Year', value: 0 }
];

interface TickProps {
  x: number;
  y: number;
  payload: any;
}

const CustomizedAxisTick = ({ x, y, payload }: TickProps) => {
  return (
    <Text
      x={x}
      y={y + 20}
      width={75}
      textAnchor="middle"
      verticalAnchor="start"
    >
      {payload.value}
    </Text>
  );
};

interface ForecastGrossInvReturnsProps {
  data: Array<{ name: string; value: number }>;
}
const ForecastGrossInvReturns = ({ data }: ForecastGrossInvReturnsProps) => {
  const theme = useTheme() as any;
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useOnScreen(ref);
  const isDataProvided = data && data.length > 0;
  const [dataToRender, setDataToRender] = useState(dummyData);
  const [initFire, setInitFire] = useState(false);

  useEffect(() => {
    if (isDataProvided) {
      setDataToRender(data);
      setTimeout(() => {
        setInitFire(true);
      }, 1500);
    }
    // eslint-disable-next-line
  }, [isDataProvided]);

  useEffect(() => {
    // eslint-disable-next-line
    if (initFire) setDataToRender(data);
    // eslint-disable-next-line
  }, [data]);

  return (
    <div ref={ref}>
      <ResponsiveContainer width="100%" height={250}>
        <BarChart
          data={dataToRender}
          margin={{ top: 20, right: 0, left: -20, bottom: 25 }}
          barCategoryGap={5}
        >
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            interval={0}
            tick={(props: TickProps) => (
              <CustomizedAxisTick
                x={props.x}
                y={props.y}
                payload={props.payload}
              />
            )}
          />
          <YAxis unit="%" axisLine={false} tickLine={false} />
          <Tooltip
            formatter={(val: string) => formatPercent(val)}
            contentStyle={tooltipStylePadded}
          />

          <Bar
            dataKey="value"
            isAnimationActive={isVisible}
            radius={radiusVertical}
            label={({ x, y, width, value, height, fill, offset }: any) => {
              const isValueNegative = value < 0;
              return (
                <text
                  style={{
                    fontSize: '13px'
                  }}
                  height={height}
                  className="recharts-text recharts-label"
                  x={x + width / 2}
                  dy={isValueNegative ? '0.71em' : '0em'}
                  fill={fill}
                  y={isValueNegative ? y + offset : y - offset}
                  textAnchor="middle"
                >
                  <tspan
                    x={x + width / 2}
                    dy={isValueNegative ? '0.71em' : '0em'}
                  >
                    {formatPercent(value)}
                  </tspan>
                </text>
              );
            }}
          >
            {dataToRender.map((_entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={
                  isDataProvided
                    ? theme.palette.baseColors.charts['risk-analysis'][index]
                    : emptyColor
                }
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ForecastGrossInvReturns;
